import { encode } from "gpt-token-utils";
import {
  ChatCompletionRequestMessage,
  Configuration,
  OpenAIApi,
  CreateChatCompletionResponse,
} from "openai";
import { OpenAIExt } from "openai-ext";
import { ProviderSettings, Settings } from "../db";
import { Provider, ProviderModel, providerRegistry } from "../utils/providers";

// Define custom models (empty by default, will be configured by user)
const customModels: ProviderModel[] = [
  {
    id: "custom-model",
    name: "Custom Model",
    description: "User-defined custom model",
    contextWindow: 4096,
    maxTokens: 4096,
  },
];

// Helper function to get provider settings from the main settings object
function getProviderSettings(settings: Settings): ProviderSettings {
  const providerSettings = settings.providerSettings?.[settings.provider || 'custom'];
  if (!providerSettings) {
    throw new Error("Provider settings not found");
  }
  return providerSettings;
}

// Helper function to get custom client
function getCustomClient(settings: Settings): OpenAIApi {
  const providerSettings = getProviderSettings(settings);
  
  const apiKey = providerSettings.apiKey || '';
  const apiAuth = providerSettings.apiAuth || 'bearer-token';
  const basePath = providerSettings.apiBase;
  
  if (!basePath) {
    throw new Error("API Base URL is required");
  }
  
  const configuration = new Configuration({
    ...(apiAuth === "bearer-token" && { apiKey }),
    basePath,
  });
  return new OpenAIApi(configuration);
}

// Helper function to set stream content
function setStreamContent(
  messageId: string,
  content: string,
  isFinal: boolean,
  db: any
) {
  content = isFinal ? content : content + "█";
  db.messages.update(messageId, { content: content });
}

// Helper function to set total tokens
function setTotalTokens(chatId: string, content: string, db: any) {
  let total_tokens = encode(content).length;
  db.chats.where({ id: chatId }).modify((chat: any) => {
    if (chat.totalTokens) {
      chat.totalTokens += total_tokens;
    } else {
      chat.totalTokens = total_tokens;
    }
  });
}

// Create the custom provider
const customProvider: Provider = {
  id: "custom",
  name: "Custom",
  description: "Custom API endpoint (e.g., Azure OpenAI)",
  authType: "bearer-token",
  defaultModel: "custom-model",
  models: customModels,
  
  // Fetch models from custom API endpoint
  async fetchModels(settings: Settings): Promise<ProviderModel[]> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      // Ensure we have an API base URL
      if (!providerSettings.apiBase) {
        console.warn("No API Base URL configured for custom provider");
        return customModels;
      }
      
      const client = getCustomClient(settings);
      
      // Different custom endpoints (like Azure) might have different API structures
      // So we'll try multiple approaches
      
      // 1. Try the standard OpenAI-compatible endpoint
      try {
        const response = await client.listModels();
        
        if (response.data && response.data.data) {
          // Convert to our model format
          const models = response.data.data.map((model: any) => ({
            id: model.id,
            name: model.id,
            description: `Custom model ${model.id}`,
            contextWindow: 4096, // Default value
            maxTokens: 4096, // Default value
          }));
          
          console.log(`Fetched ${models.length} models from custom API endpoint`);
          return models;
        }
      } catch (error: any) {
        console.warn("Standard model fetch failed:", error.message);
        // Continue with alternative approaches
      }
      
      // 2. Try Azure OpenAI-specific endpoint with API version
      // For Azure, the endpoint might be: /openai/deployments
      if (providerSettings.apiBase.includes('azure') && providerSettings.apiVersion) {
        try {
          // Construct Azure-specific URL for deployments
          const azureBaseUrl = providerSettings.apiBase.endsWith('/') 
            ? providerSettings.apiBase 
            : `${providerSettings.apiBase}/`;
          
          const url = `${azureBaseUrl}openai/deployments?api-version=${providerSettings.apiVersion}`;
          
          // Make direct fetch with proper headers
          const headers: Record<string, string> = {
            'Content-Type': 'application/json'
          };
          
          // Add API key with the right header based on auth type
          if (providerSettings.apiAuth === 'api-key' && providerSettings.apiKey) {
            headers['api-key'] = providerSettings.apiKey;
          } else if (providerSettings.apiKey) {
            headers['Authorization'] = `Bearer ${providerSettings.apiKey}`;
          }
          
          const response = await fetch(url, { headers });
          
          if (response.ok) {
            const data = await response.json();
            
            // Azure returns deployments in a different format
            if (data.value) {
              const models = data.value.map((deployment: any) => ({
                id: deployment.id || deployment.name, // Azure uses deployment name as ID
                name: deployment.name || deployment.id,
                description: `Azure deployment: ${deployment.name}`,
                contextWindow: 4096, // Default value
                maxTokens: 4096, // Default value
              }));
              
              console.log(`Fetched ${models.length} deployments from Azure OpenAI`);
              return models;
            }
          } else {
            console.warn(`Azure API returned status ${response.status}`);
          }
        } catch (error: any) {
          console.warn("Azure-specific model fetch failed:", error.message);
        }
      }
      
      // 3. If all attempts failed but we have a model configured, create a model entry for it
      if (providerSettings.model) {
        console.log(`Using configured model: ${providerSettings.model}`);
        return [
          {
            id: providerSettings.model,
            name: providerSettings.model,
            description: `Custom model ${providerSettings.model}`,
            contextWindow: 4096,
            maxTokens: 4096,
          },
        ];
      }
      
      // 4. If everything else fails, return default models
      return customModels;
    } catch (error: any) {
      console.error("Error fetching custom models:", error);
      return customModels;
    }
  },

  // Validate credentials
  async validateCredentials(settings: Settings): Promise<boolean> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiBase) {
        throw new Error("API Base URL is required");
      }

      // For custom endpoints, API key might be optional depending on the auth type
      if (providerSettings.apiAuth === "bearer-token" && !providerSettings.apiKey) {
        throw new Error("API Key is required for Bearer Token authentication");
      }

      const client = getCustomClient(settings);

      // Use a model name that's likely to exist on the custom endpoint
      const modelName = settings.model || providerSettings.model || "custom-model";

      await client.createChatCompletion(
        {
          model: modelName,
          messages: [{ role: "user", content: "Hello" }],
          max_tokens: 1,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(providerSettings.apiAuth === "api-key" && providerSettings.apiKey && {
              "api-key": providerSettings.apiKey,
            }),
          },
          params: {
            ...(providerSettings.apiVersion && { "api-version": providerSettings.apiVersion }),
          },
        }
      );

      return true;
    } catch (error: any) {
      console.error("Custom API validation error:", error);
      const message = error.response?.data?.error?.message || error.message;
      throw new Error(`Custom API validation failed: ${message}`);
    }
  },

  // Create chat completion
  async createChatCompletion(
    settings: Settings,
    messages: ChatCompletionRequestMessage[]
  ): Promise<CreateChatCompletionResponse> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiBase) {
        throw new Error("API Base URL is required");
      }

      if (providerSettings.apiAuth === "bearer-token" && !providerSettings.apiKey) {
        throw new Error("API Key is required for Bearer Token authentication");
      }

      if (!settings.model) {
        throw new Error("Model is not configured");
      }

      const client = getCustomClient(settings);

      const response = await client.createChatCompletion(
        {
          model: settings.model,
          messages,
          stream: false,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(providerSettings.apiAuth === "api-key" && providerSettings.apiKey && {
              "api-key": providerSettings.apiKey,
            }),
          },
          params: {
            ...(providerSettings.apiVersion && { "api-version": providerSettings.apiVersion }),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Custom API chat completion error:", error);
      const message = error.response?.data?.error?.message || error.message;
      throw new Error(`Custom API chat completion failed: ${message}`);
    }
  },

  // Create streaming chat completion
  async createStreamChatCompletion(
    settings: Settings,
    messages: ChatCompletionRequestMessage[],
    chatId: string,
    messageId: string
  ): Promise<any> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiBase) {
        throw new Error("API Base URL is required");
      }

      if (providerSettings.apiAuth === "bearer-token" && !providerSettings.apiKey) {
        throw new Error("API Key is required for Bearer Token authentication");
      }

      if (!settings.model) {
        throw new Error("Model is not configured");
      }

      // We need to import db here to avoid circular dependency
      const { db } = await import("../db");

      // For custom endpoints, we'll use a non-streaming approach since OpenAIExt
      // doesn't support custom base URLs directly
      db.messages.update(messageId, { content: "Generating response..." });

      const client = getCustomClient(settings);

      const response = await client.createChatCompletion(
        {
          model: settings.model,
          messages,
          stream: false,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(providerSettings.apiAuth === "api-key" && providerSettings.apiKey && {
              "api-key": providerSettings.apiKey,
            }),
          },
          params: {
            ...(providerSettings.apiVersion && { "api-version": providerSettings.apiVersion }),
          },
        }
      );

      const content = response.data.choices[0].message?.content || "";
      
      // Update the message with the complete response
      db.messages.update(messageId, { content });
      
      // Update token count
      if (response.data.usage) {
        db.chats.where({ id: chatId }).modify((chat: any) => {
          if (chat.totalTokens) {
            chat.totalTokens += response.data.usage!.total_tokens;
          } else {
            chat.totalTokens = response.data.usage!.total_tokens;
          }
        });
      } else {
        // If usage is not provided, estimate based on content length
        setTotalTokens(chatId, content, db);
      }

      return response.data;
    } catch (error: any) {
      console.error("Custom API streaming error:", error);
      const message = error.response?.data?.error?.message || error.message;
      
      // We need to import db here to avoid circular dependency
      const { db } = await import("../db");
      db.messages.update(messageId, { content: `Error: ${message}` });
      
      throw new Error(`Custom API streaming failed: ${message}`);
    }
  },
};

// Register the custom provider
providerRegistry.register(customProvider);

export default customProvider;
